import React from 'react';
import { notifyAnalytics } from '../../notify-analytics';
import { NavLink } from '../NavLink/NavLink';
import { NavList } from '../../mobile-menu/NavList/NavList';
import { NavigationItemType } from '../itemTypes';
import { Heading } from '../../mobile-menu/Heading/Heading';
import { SHOP_DEPARTMENT, EXTRAS_DEPARTMENT, Category } from '../../constants';
import { allChildrenHaveBrandLogos } from '../../../utils/utils';

export type MobileMenuItemProps = {
  index: number;
  childItems?: MenuItem[];
  children?: React.ReactNode;
  hierarchy: string[];
  itemType?: NavigationItemType;
  label: string;
  level?: number;
  onClick?: (...args: unknown[]) => void;
  type?: string;
  url?: string;
  imageUrl?: string;
  activeTab?: string;
  isTabbable?: boolean;
};

export const MobileMenuItem = ({
  index,
  childItems = undefined,
  children = undefined,
  hierarchy = [],
  itemType = NavigationItemType.DEFAULT,
  label,
  level = 0,
  onClick = undefined,
  url = '',
  imageUrl = '',
  activeTab = '',
  isTabbable = true,
}: MobileMenuItemProps) => {
  const hasChildren = childItems?.length > 0;

  const sendAnalytics = () => {
    notifyAnalytics([...hierarchy, label].join('|'));
  };

  const advanceToChild = (...args: unknown[]) => {
    sendAnalytics();
    onClick?.(...args);
  };

  let showInlineMenu = false;
  let childItemType = NavigationItemType.DEFAULT;

  const childItemsAllHaveBrandLogos =
    childItems?.length && allChildrenHaveBrandLogos(childItems);

  if (childItemsAllHaveBrandLogos) {
    showInlineMenu = true;
    childItemType = NavigationItemType.BRAND_LOGOS;
  } else if (label === SHOP_DEPARTMENT) {
    showInlineMenu = true;
    childItemType = NavigationItemType.IMAGE_GRID;
  } else if (label === EXTRAS_DEPARTMENT) {
    showInlineMenu = true;
    childItemType = NavigationItemType.EXTRAS;
  }

  const getChild = (): React.ReactNode => {
    if (showInlineMenu) {
      const showHeading = !(activeTab === Category.SHOP && level === 0);
      return (
        <>
          {showHeading && <Heading label={label} />}
          <NavList
            inlineChild
            itemType={childItemType}
            name={`submenu-${label.toLowerCase()}`}
          >
            {childItems.map((nestedChild, index) => {
              const itemName =
                nestedChild.name === Category.SERVICES_EXPERIENCES
                  ? Category.SERVICES
                  : nestedChild.name;
              return (
                <MobileMenuItem
                  index={index}
                  key={`${level + 1}-${itemName}`}
                  level={level + 1}
                  label={itemName}
                  onClick={() => onClick?.(nestedChild)}
                  childItems={nestedChild.child}
                  itemType={childItemType}
                  type={nestedChild.type}
                  url={nestedChild.url}
                  imageUrl={nestedChild.imageUrl}
                  hierarchy={hierarchy}
                  activeTab={activeTab}
                  isTabbable={isTabbable}
                />
              );
            })}
          </NavList>
        </>
      );
    }

    return (
      <NavLink
        index={index}
        tab={activeTab}
        itemType={itemType}
        label={label}
        {...(hasChildren ? {} : { url })}
        handleClick={hasChildren ? advanceToChild : sendAnalytics}
        hierarchy={hierarchy}
        imageUrl={imageUrl}
        tabIndex={isTabbable ? undefined : -1}
      />
    );
  };

  return (
    <>
      {getChild()}
      {children}
    </>
  );
};
